import React, { ReactElement, useEffect, useRef, useState } from 'react'

import Table from '@instabox/internal-web-ui/components/Table'
import { GemfuryPackage } from '@instabox/lockers-update-common/types'
import { formatISO9075 } from 'date-fns'
import { useParams } from 'react-router-dom'

import fetch from '../../fetch'
import PackageVersionDetailsButton from '../components/PackageVersionDetailsButton'
import TableActionsContainer from '../components/TableActionsContainer'

import VersionChart, { VersionChartDatapoint } from './components/VersionChart'

interface PackageParams {
  packageId: string
}

function Package(): ReactElement {
  const { packageId }: PackageParams = useParams()
  const containerRef = useRef<HTMLDivElement>(null)

  const [pkg, setPkg] = useState<GemfuryPackage | undefined>(undefined)
  const [usage, setUsage] = useState<VersionChartDatapoint[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const getPackage = async (): Promise<void> => {
      setLoading(true)

      try {
        const response = await fetch(`/api/package/${packageId}`)

        if (!response.ok) {
          throw new Error(`Unable to get packages: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()

        setPkg(data)
      } finally {
        setLoading(false)
      }
    }

    getPackage().catch(console.error)
  }, [packageId])

  useEffect(() => {
    const getUsage = async (): Promise<void> => {
      const response = await fetch(`/api/package/${packageId}/usage`)

      if (!response.ok) {
        throw new Error(`Unable to get package usage: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      setUsage(data)
    }

    getUsage().catch(console.error)
  }, [packageId])

  const versions = [
    ...(pkg?.versions ?? []),
    {
      version: 'unknown',
      createdAt: undefined,
      language: '',
      createdBy: '',
    },
  ]

  return (
    <div ref={containerRef}>
      <h3>{packageId}</h3>
      <VersionChart data-testid="versionChart" height={400} width={containerRef.current?.offsetWidth} data={usage} />
      <Table
        isLoading={loading}
        headers={{
          version: 'Version',
          language: 'Language',
          createdAt: 'Created at',
          createdBy: 'Created by',
          usage: 'Number of users',
          actions: '',
        }}
        rows={versions
          .map((pkg) => ({
            ...pkg,
            usage: usage.find((u) => u.version === pkg.version)?.count,
            disableCheckbox: true,
            createdAt: pkg.createdAt ? formatISO9075(new Date(pkg.createdAt)) : '',
            actions: (
              <TableActionsContainer>
                <PackageVersionDetailsButton version={pkg.version} name={packageId} />
              </TableActionsContainer>
            ),
          }))
          // Hide any rows that do have no users
          .filter((r) => r.usage)}
      />
    </div>
  )
}

export default Package
